/**
 * /play route — live wiring with pacing and inline showdown.
 *
 * Visible states:
 *   - awaiting-action: normal table, hero acts via ActionBar / keyboard.
 *   - between-hands: table revealed (all hole cards, 🏆 on winner,
 *     inline winnerLine on the felt). Footer ActionBar swaps to a single
 *     `HandEndNextButton` — no layout shift, click or Space/Enter to
 *     continue.
 *   - hero eliminated (busted) OR finished: FinalSummary with
 *     "새 토너먼트 시작" button; navigates to /play?seed=<new> which
 *     useAutoStartTournament picks up to boot a fresh game.
 */
import { useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CHARACTER_ORDER, CHARACTERS, codenameForPlayerId } from '@/characters/definitions';
import { ActionBar } from '@/components/actions/ActionBar';
import { Hud } from '@/components/hud/Hud';
import { ActionLog } from '@/components/play/ActionLog';
import { FinalSummary } from '@/components/play/FinalSummary';
import { HandEndNextButton } from '@/components/play/HandEndNextButton';
import { HeroPanel } from '@/components/player/HeroPanel';
import { OpponentRing } from '@/components/player/OpponentRing';
import { Table } from '@/components/table/Table';
import { createPokersolverEvaluator } from '@/engine/handEvaluator';
import type { HandEvaluator, Rng } from '@/engine/ports';
import { createRng } from '@/engine/rng';
import type { Action, PlayerId } from '@/engine/types';
import { PERSONALITIES } from '@/gto/personality';
import { createGtoBot } from '@/gto/solver';
import type { PersonalityName } from '@/gto/types';
import { useAssistant } from '@/hooks/useAssistant';
import { useAutoStartTournament } from '@/hooks/useAutoStartTournament';
import { useBotDriver } from '@/hooks/useBotDriver';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import { useLegalActions } from '@/hooks/useLegalActions';
import { useStreetActions } from '@/hooks/useStreetActions';
import { useTableView } from '@/hooks/useTableView';
import { PixiStage } from '@/pixi/Stage';
import { useGameStore } from '@/state/gameStore';
import { toKoreanHandDescription } from '@/utils/handDescription';

const HERO_ID: PlayerId = 'p0';
const DEFAULT_SEED = 'play-default';
const PLAYER_IDS: ReadonlyArray<PlayerId> = ['p0', 'p1', 'p2', 'p3', 'p4', 'p5'];

function heroCharacter(): PersonalityName {
  return 'ORACLE';
}

function useBotMap(seed: string): (playerId: PlayerId) => ReturnType<typeof createGtoBot> {
  return useMemo(() => {
    const evaluator: HandEvaluator = createPokersolverEvaluator();
    const cache = new Map<PlayerId, ReturnType<typeof createGtoBot>>();
    return (playerId: PlayerId) => {
      let bot = cache.get(playerId);
      if (!bot) {
        const character = codenameForPlayerId(playerId);
        const rng: Rng = createRng(`${seed}-bot-${playerId}`);
        bot = createGtoBot(PERSONALITIES[character], { rng, evaluator, trials: 600 });
        cache.set(playerId, bot);
      }
      return bot;
    };
  }, [seed]);
}

export function PlayRoute() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const seed = searchParams.get('seed') ?? DEFAULT_SEED;
  const tournamentId = `tourney-${seed}`;

  const autoStartOptions = useMemo(
    () => ({
      tournamentId,
      seed,
      playerIds: PLAYER_IDS,
      heroId: HERO_ID,
      characterCodenames: [heroCharacter(), ...CHARACTER_ORDER],
    }),
    [tournamentId, seed],
  );
  useAutoStartTournament(autoStartOptions);

  const getBot = useBotMap(seed);
  useBotDriver({ heroId: HERO_ID, getBot });
  useAssistant({ heroId: HERO_ID });

  const view = useTableView(HERO_ID);
  const legals = useLegalActions(HERO_ID);
  const status = useGameStore((s) => s.status);
  const lastHandSummary = useGameStore((s) => s.lastHandSummary);
  const hand = useGameStore((s) => s.hand);
  const { labels: streetActionLabels } = useStreetActions();

  const isBetweenHands = status === 'between-hands' && lastHandSummary !== null;
  const isFinished = status === 'finished';
  const showFinalSummary = isFinished || (view.heroEliminated && !isBetweenHands);

  // Opponent enrichment: street contribution chips, street action labels,
  // hole-card reveal + winner highlight during between-hands.
  // Real-poker rule: folders muck — only non-folded players reveal at hand-end.
  const opponentsWithLabels = useMemo(() => {
    const foldedSet = new Set(lastHandSummary?.foldedIds ?? []);
    return view.opponents.map((o, i) => {
      const playerId = view.opponentPlayerIds[i];
      const streetActionLabel = playerId ? streetActionLabels[playerId] : undefined;
      const isWinner = !!(
        isBetweenHands &&
        playerId &&
        lastHandSummary?.winnerIds.includes(playerId)
      );
      const didFold = playerId ? foldedSet.has(playerId) : false;
      const revealedCards =
        isBetweenHands && playerId && !didFold
          ? lastHandSummary?.holeCardsByPlayer[playerId]
          : undefined;
      return {
        ...o,
        streetActionLabel,
        isThinking: o.isTurn && !isBetweenHands,
        isWinner,
        holeCards: revealedCards ?? o.holeCards,
        hideHoleCards: revealedCards ? false : o.hideHoleCards,
      };
    });
  }, [view.opponents, view.opponentPlayerIds, streetActionLabels, isBetweenHands, lastHandSummary]);

  // During showdown, reveal hero's own hole cards too — unless hero mucked.
  const heroFolded = !!lastHandSummary?.foldedIds.includes(HERO_ID);
  const heroHoleCards =
    isBetweenHands && lastHandSummary && !heroFolded
      ? lastHandSummary.holeCardsByPlayer[HERO_ID]
      : view.heroHoleCards;
  const heroIsWinner =
    isBetweenHands && lastHandSummary ? lastHandSummary.winnerIds.includes(HERO_ID) : false;

  const handleAction = useCallback(async (action: Action) => {
    await useGameStore.getState().applyAction(action);
  }, []);

  useKeyboardShortcuts({
    enabled: status === 'awaiting-action' && view.isHeroTurn,
    playerId: HERO_ID,
    legals,
    onAction: handleAction,
  });

  const characterLabelFor = useCallback(
    (playerId: PlayerId): string => {
      if (playerId === HERO_ID) return '당신';
      const idx = view.opponentPlayerIds.indexOf(playerId);
      if (idx < 0) return playerId;
      const codename = view.opponents[idx]?.codename;
      return codename ? CHARACTERS[codename].displayName : playerId;
    },
    [view.opponentPlayerIds, view.opponents],
  );

  const handleNewTournament = useCallback(() => {
    const freshSeed = `play-${Date.now().toString(36)}`;
    // gameStore.reset() so useAutoStartTournament sees a clean slate on
    // the remounted URL. The navigate below flips the seed param which
    // drives useAutoStartTournament's new-id branch.
    useGameStore.getState().reset();
    navigate(`/play?seed=${encodeURIComponent(freshSeed)}`, { replace: true });
  }, [navigate]);

  const potValue =
    isBetweenHands && lastHandSummary
      ? lastHandSummary.payouts.reduce((sum, p) => sum + p.amount, 0)
      : view.pot;

  const winnerLine = useMemo(() => {
    if (!isBetweenHands || !lastHandSummary) return null;
    const { winnerIds, payouts, winningDescription } = lastHandSummary;
    if (winnerIds.length === 0) return null;
    const perWinner = winnerIds.map((id) => {
      const won = payouts.filter((p) => p.playerId === id).reduce((s, p) => s + p.amount, 0);
      return `${characterLabelFor(id)} +${won}`;
    });
    const koreanDesc = toKoreanHandDescription(winningDescription);
    // If we have a localized hand rank, show it. Otherwise the hand
    // ended by fold (no showdown, no description) → say so explicitly
    // so the UI doesn't leave the reason for the win implicit.
    const trailer = koreanDesc ? ` · ${koreanDesc}` : ' · 상대 폴드';
    return `🏆 ${perWinner.join(' · ')}${trailer}`;
  }, [isBetweenHands, lastHandSummary, characterLabelFor]);

  const handleNextHand = useCallback(() => {
    void useGameStore.getState().advanceHand();
  }, []);

  const bbSize = view.blinds.bb ?? 0;
  const boardCards = isBetweenHands && lastHandSummary ? lastHandSummary.board : view.board;

  return (
    <main
      className="h-dvh bg-bg text-fg flex flex-col relative overflow-hidden"
      data-testid="play-route"
    >
      <Hud
        level={view.level || undefined}
        blinds={view.blinds.bb ? view.blinds : undefined}
        handNumber={view.handNumber}
        pot={potValue}
        currentTurnLabel={
          !isBetweenHands && !showFinalSummary && status === 'awaiting-action' && view.pendingActor
            ? characterLabelFor(view.pendingActor)
            : null
        }
      />
      <Table
        pot={potValue}
        board={boardCards}
        winnerLine={winnerLine}
        bbSize={bbSize}
        heroStreetContribution={view.heroStreetContribution}
        heroStreetActionLabel={streetActionLabels[HERO_ID]}
        dealerSeat={view.dealerSeat}
        overlay={
          hand ? <ActionLog actions={hand.actionHistory} handNumber={hand.handNumber} /> : null
        }
      >
        <PixiStage>
          <OpponentRing opponents={opponentsWithLabels} bbSize={bbSize} />
        </PixiStage>
      </Table>
      {/* Hero hand row — below the table (outside the felt). Contains:
          hero's hole cards (xl) + AI mascot + stack. */}
      <HeroPanel
        stack={view.heroStack}
        holeCards={heroHoleCards}
        bbSize={bbSize}
        isTurn={view.isHeroTurn}
        isWinner={heroIsWinner}
      />
      {showFinalSummary ? (
        <FinalSummary
          heroId={HERO_ID}
          mode={isFinished ? 'finished' : 'eliminated'}
          onNewTournament={handleNewTournament}
          characterLabelFor={characterLabelFor}
        />
      ) : (
        <>
          {/* ActionBar is ALWAYS rendered — disabled when it's not the
              hero's turn or the hand is between-hands. Keeps the page
              layout stable; buttons visibly dim instead of disappearing. */}
          <ActionBar
            legals={legals}
            playerId={HERO_ID}
            pot={view.pot}
            bbSize={bbSize}
            onAction={handleAction}
            disabled={!view.isHeroTurn || isBetweenHands}
          />
          {isBetweenHands && lastHandSummary ? <HandEndNextButton onNext={handleNextHand} /> : null}
        </>
      )}
    </main>
  );
}
